import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import DirectionsRailwayIcon from "@mui/icons-material/DirectionsRailway";
import YardIcon from "@mui/icons-material/Yard";
import BuildIcon from "@mui/icons-material/Build";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";

// =====================================================
// DUMMY DATA
// =====================================================

const stats = [
  {
    title: "Coaches in Workshop",
    value: 24,
    subtitle: "Currently in custody",
    icon: <DirectionsRailwayIcon />,
    color: "primary",
  },
  {
    title: "In Yard",
    value: 7,
    subtitle: "Awaiting shop allocation",
    icon: <YardIcon />,
    color: "warning",
  },
  {
    title: "Under Repair",
    value: 17,
    subtitle: "Work in progress",
    icon: <BuildIcon />,
    color: "success",
  },
  {
    title: "Under POH",
    value: 12,
    subtitle: "Active POH jobs",
    icon: <EngineeringIcon />,
    color: "info",
  },
  {
    title: "Inspection",
    value: 4,
    subtitle: "Awaiting inspection",
    icon: <FactCheckIcon />,
    color: "secondary",
  },
  {
    title: "Ready Outturn",
    value: 3,
    subtitle: "Ready for gate out",
    icon: <ExitToAppIcon />,
    color: "error",
  },
];

const currentCoaches = [
  { coachNo: "12345", visitNo: "2026-00123", location: "Shell Shop", status: "WORKING", days: 8 },
  { coachNo: "12346", visitNo: "2026-00124", location: "Yard", status: "WAITING", days: 3 },
  { coachNo: "12347", visitNo: "2026-00125", location: "Electrical Shop", status: "WORKING", days: 11 },
  { coachNo: "12348", visitNo: "2026-00126", location: "Inspection", status: "INSPECTION", days: 14 },
  { coachNo: "12349", visitNo: "2026-00127", location: "Yard", status: "WAITING", days: 2 },
];

const shopData = [
  { name: "Shell Shop", count: 6, percentage: 35, color: "primary" },
  { name: "Electrical Shop", count: 4, percentage: 24, color: "info" },
  { name: "Mechanical Shop", count: 3, percentage: 18, color: "success" },
  { name: "Paint Shop", count: 2, percentage: 12, color: "warning" },
  { name: "Carpentry", count: 2, percentage: 12, color: "secondary" },
];

const movements = [
  { coachNo: "12345", from: "Yard", to: "Shell Shop", time: "10:15", status: "WORKING" },
  { coachNo: "12347", from: "Yard", to: "Electrical Shop", time: "09:40", status: "WORKING" },
  { coachNo: "12348", from: "Electrical Shop", to: "Inspection", time: "09:10", status: "INSPECTION" },
  { coachNo: "12350", from: "Shell Shop", to: "Yard", time: "08:55", status: "WAITING" },
];

const activities = [
  { title: "Coach 12345 moved to Shell Shop", time: "10:15 AM", type: "Movement" },
  { title: "Coach 12348 sent for inspection", time: "09:10 AM", type: "Inspection" },
  { title: "Coach 12347 electrical work started", time: "08:50 AM", type: "Work" },
  { title: "Coach 12350 received at yard", time: "08:25 AM", type: "Gate In" },
];

const dailySummary = [
  { label: "Gate In Today", value: 5, color: "primary" },
  { label: "Shop Movements", value: 18, color: "info" },
  { label: "Inspections", value: 4, color: "warning" },
  { label: "Gate Out Today", value: 2, color: "success" },
];

// =====================================================
// HELPERS
// =====================================================

const getStatusColor = (status) => {
  switch (status) {
    case "WORKING":
      return "success";
    case "WAITING":
      return "warning";
    case "INSPECTION":
      return "info";
    case "READY":
      return "primary";
    default:
      return "default";
  }
};

// =====================================================
// SHARED STYLES
// =====================================================

const cardSx = {
  borderRadius: { xs: 2.5, sm: 3 },
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.03)",
  background:
    "linear-gradient(145deg, rgba(255,255,255,1) 0%, rgba(249,251,255,1) 100%)",
  overflow: "hidden",
};

const sectionContentSx = {
  p: { xs: 1.5, sm: 2, md: 2.25 },
  "&:last-child": {
    pb: { xs: 1.5, sm: 2, md: 2.25 },
  },
};

// =====================================================
// KPI CARD
// =====================================================

function KPI({ item }) {
  return (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        width: "100%",
        height: "100%",
        minHeight: { xs: 122, sm: 138, lg: 148 },
        position: "relative",
        transition: "transform .2s ease, box-shadow .2s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: `${item.color}.main`,
        },
        "&:hover": {
          transform: { xs: "none", sm: "translateY(-3px)" },
          boxShadow: { xs: "none", sm: "0 10px 28px rgba(15,23,42,.08)" },
        },
      }}
    >
      <CardContent sx={{ ...sectionContentSx, height: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{ height: "100%" }}
        >
          <Box sx={{ minWidth: 0, pr: 0.75 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              sx={{
                display: "block",
                lineHeight: 1.25,
                fontSize: { xs: "0.66rem", sm: "0.72rem", lg: "0.75rem" },
              }}
            >
              {item.title}
            </Typography>

            <Typography
              sx={{
                mt: 0.55,
                fontSize: { xs: "1.65rem", sm: "1.9rem", lg: "2.05rem" },
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-0.03em",
              }}
            >
              {item.value}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.65,
                lineHeight: 1.2,
                fontSize: { xs: "0.6rem", sm: "0.67rem", lg: "0.7rem" },
              }}
            >
              {item.subtitle}
            </Typography>
          </Box>

          <Box
            sx={{
              width: { xs: 32, sm: 38, lg: 42 },
              height: { xs: 32, sm: 38, lg: 42 },
              borderRadius: { xs: 1.8, sm: 2.2 },
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${item.color}.50`,
              color: `${item.color}.main`,
            }}
          >
            {item.icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// =====================================================
// SECTION HEADER
// =====================================================

function SectionHeader({ icon, title, subtitle, action }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={{ xs: 1, sm: 1.5 }}
      sx={{ mb: { xs: 1.5, sm: 1.8 } }}
    >
      <Stack
        direction="row"
        spacing={1.1}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Box
          sx={{
            width: { xs: 34, sm: 38 },
            height: { xs: 34, sm: 38 },
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: "primary.50",
            color: "primary.main",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={750}
            sx={{
              fontSize: { xs: "0.98rem", sm: "1.05rem" },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ lineHeight: 1.2 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      </Stack>

      {action && (
        <Box sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}>
          {action}
        </Box>
      )}
    </Stack>
  );
}

// =====================================================
// CURRENT COACH MOBILE CARD
// =====================================================

function CurrentCoachMobileCard({ coach }) {
  return (
    <Box
      sx={{
        p: 1.35,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={800} noWrap>
            Coach {coach.coachNo}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            Visit {coach.visitNo}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={coach.status}
          color={getStatusColor(coach.status)}
          variant="outlined"
          sx={{ fontWeight: 700, height: 25, flexShrink: 0 }}
        />
      </Stack>

      <Divider sx={{ my: 1.1 }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Stack
          direction="row"
          spacing={0.55}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <LocationOnIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600} noWrap>
            {coach.location}
          </Typography>
        </Stack>

        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: "15px !important" }} />}
          label={`${coach.days} days`}
          size="small"
          variant="outlined"
          sx={{ height: 25, fontWeight: 700, flexShrink: 0 }}
        />
      </Stack>
    </Box>
  );
}

// =====================================================
// MOVEMENT MOBILE CARD
// =====================================================

function MovementMobileCard({ movement }) {
  return (
    <Box
      sx={{
        p: 1.35,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2.5,
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <Typography fontWeight={800}>Coach {movement.coachNo}</Typography>

        <Chip
          size="small"
          label={movement.status}
          color={getStatusColor(movement.status)}
          variant="outlined"
          sx={{ fontWeight: 700, height: 25 }}
        />
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.65}
        sx={{ mt: 1.1, minWidth: 0 }}
      >
        <Chip
          label={movement.from}
          size="small"
          variant="outlined"
          sx={{
            maxWidth: "38%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />

        <ArrowForwardIcon fontSize="small" color="action" />

        <Chip
          label={movement.to}
          size="small"
          color="primary"
          variant="outlined"
          sx={{
            maxWidth: "38%",
            "& .MuiChip-label": {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />

        <Box sx={{ flex: 1 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={700}
          sx={{ flexShrink: 0 }}
        >
          {movement.time}
        </Typography>
      </Stack>
    </Box>
  );
}

// =====================================================
// SHOP WORKLOAD
// =====================================================

function ShopWorkload() {
  return (
    <Card elevation={0} sx={{ ...cardSx, height: "100%" }}>
      <CardContent sx={sectionContentSx}>
        <SectionHeader
          icon={<BuildIcon />}
          title="Shop Workload"
          subtitle="Current active work"
          action={
            <Chip
              label="17 Coaches"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 700, height: 28 }}
            />
          }
        />

        <Stack spacing={1.1}>
          {shopData.map((shop) => (
            <Box
              key={shop.name}
              sx={{
                p: { xs: 1.15, sm: 1.35 },
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2.5,
                bgcolor: "background.paper",
                transition: "all .2s ease",
                "&:hover": {
                  borderColor: `${shop.color}.light`,
                  transform: { xs: "none", sm: "translateY(-1px)" },
                  boxShadow: {
                    xs: "none",
                    sm: "0 5px 16px rgba(15,23,42,.06)",
                  },
                },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0 }}
              >
                <Box
                  sx={{
                    width: { xs: 32, sm: 35 },
                    height: { xs: 32, sm: 35 },
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: `${shop.color}.50`,
                    color: `${shop.color}.main`,
                  }}
                >
                  <BuildIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {shop.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Active workload
                  </Typography>
                </Box>

                <Chip
                  label={shop.count}
                  size="small"
                  color={shop.color}
                  sx={{
                    minWidth: 34,
                    height: 27,
                    borderRadius: 1.5,
                    fontWeight: 800,
                    flexShrink: 0,
                  }}
                />

                <Typography
                  variant="caption"
                  fontWeight={800}
                  color="text.secondary"
                  sx={{
                    minWidth: 34,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {shop.percentage}%
                </Typography>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={shop.percentage}
                color={shop.color}
                sx={{
                  mt: 1,
                  height: { xs: 5, sm: 6 },
                  borderRadius: 10,
                  bgcolor: "action.hover",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 10,
                  },
                }}
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// =====================================================
// CURRENT COACHES
// =====================================================

function CurrentCoaches({ isMobile }) {
  return (
    <Card elevation={0} sx={{ ...cardSx, height: "100%" }}>
      <CardContent sx={sectionContentSx}>
        <SectionHeader
          icon={<DirectionsRailwayIcon />}
          title="Current Coaches"
          subtitle="Coaches presently under workshop custody"
          action={
            <Chip
              label="24 Active"
              color="primary"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 700 }}
            />
          }
        />

        {isMobile ? (
          <Stack spacing={1}>
            {currentCoaches.map((coach) => (
              <CurrentCoachMobileCard
                key={coach.visitNo}
                coach={coach}
              />
            ))}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "action.hover",
                    "& th": {
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Coach No.</TableCell>
                  <TableCell>Visit No.</TableCell>
                  <TableCell>Current Location</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Days</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {currentCoaches.map((coach) => (
                  <TableRow key={coach.visitNo} hover>
                    <TableCell>
                      <Typography fontWeight={700}>
                        {coach.coachNo}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {coach.visitNo}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack
                        direction="row"
                        spacing={0.5}
                        alignItems="center"
                      >
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {coach.location}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={coach.status}
                        color={getStatusColor(coach.status)}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>

                    <TableCell align="right">
                      <Chip
                        label={`${coach.days} d`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 700 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================
// MOVEMENTS
// =====================================================

function RecentMovements({ isMobile }) {
  return (
    <Card elevation={0} sx={{ ...cardSx, height: "100%" }}>
      <CardContent sx={sectionContentSx}>
        <SectionHeader
          icon={<LocationOnIcon />}
          title="Recent Coach Movements"
          subtitle="Latest movements inside workshop"
          action={
            <Chip
              label="Today"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          }
        />

        {isMobile ? (
          <Stack spacing={1}>
            {movements.map((movement, index) => (
              <MovementMobileCard
                key={`${movement.coachNo}-${index}`}
                movement={movement}
              />
            ))}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "action.hover",
                    "& th": {
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Coach</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {movements.map((movement, index) => (
                  <TableRow
                    key={`${movement.coachNo}-${index}`}
                    hover
                  >
                    <TableCell>
                      <Typography fontWeight={700}>
                        {movement.coachNo}
                      </Typography>
                    </TableCell>

                    <TableCell>{movement.from}</TableCell>

                    <TableCell>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                      >
                        <ArrowForwardIcon
                          fontSize="small"
                          color="action"
                        />
                        <Typography
                          variant="body2"
                          fontWeight={600}
                        >
                          {movement.to}
                        </Typography>
                      </Stack>
                    </TableCell>

                    <TableCell>{movement.time}</TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        label={movement.status}
                        color={getStatusColor(movement.status)}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================
// RECENT ACTIVITY
// =====================================================

function RecentActivity() {
  return (
    <Card elevation={0} sx={{ ...cardSx, height: "100%" }}>
      <CardContent sx={sectionContentSx}>
        <SectionHeader
          icon={<ScheduleIcon />}
          title="Recent Activity"
          subtitle="Workshop activity today"
        />

        <Stack spacing={0}>
          {activities.map((activity, index) => (
            <Box
              key={index}
              sx={{
                py: 1.25,
                borderBottom:
                  index !== activities.length - 1
                    ? "1px solid"
                    : "none",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" spacing={1.2}>
                <Box
                  sx={{
                    mt: 0.65,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor:
                      index === 0
                        ? "primary.main"
                        : "action.disabled",
                    flexShrink: 0,
                  }}
                />

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={650}
                    sx={{ lineHeight: 1.4 }}
                  >
                    {activity.title}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.8}
                    alignItems="center"
                    sx={{ mt: 0.55 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      {activity.time}
                    </Typography>

                    <Chip
                      label={activity.type}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 21,
                        fontSize: 10,
                        fontWeight: 600,
                      }}
                    />
                  </Stack>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// =====================================================
// DAILY SUMMARY
// =====================================================

function DailySummary() {
  return (
    <Card elevation={0} sx={{ ...cardSx, mt: { xs: 1.5, sm: 2 } }}>
      <CardContent sx={sectionContentSx}>
        <SectionHeader
          icon={<TrendingUpIcon />}
          title="Today's Operational Summary"
          subtitle="Workshop activity overview"
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(4, minmax(0, 1fr))",
            },
            gap: { xs: 1.1, sm: 1.5 },
          }}
        >
          {dailySummary.map((item) => (
            <Box
              key={item.label}
              sx={{
                minWidth: 0,
                p: { xs: 1.3, sm: 1.6 },
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 3,
                  bgcolor: `${item.color}.main`,
                },
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{
                  display: "block",
                  lineHeight: 1.25,
                }}
              >
                {item.label}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: { xs: "1.45rem", sm: "1.7rem" },
                  lineHeight: 1,
                  fontWeight: 800,
                }}
              >
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// =====================================================
// MAIN DASHBOARD
// =====================================================

export default function WorkshopDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: "100vh",
        bgcolor: "background.default",
        p: {
          xs: 1,
          sm: 2,
          md: 2.5,
          lg: 3,
        },
      }}
    >
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 1.75, sm: 2.5 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.9, sm: 1.2 }}
        >
          <Box
            sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              borderRadius: { xs: 2.2, sm: 2.7 },
              bgcolor: "primary.main",
              color: "primary.contrastText",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 5px 16px rgba(25,118,210,.22)",
            }}
          >
            <DirectionsRailwayIcon
              sx={{ fontSize: { xs: 22, sm: 26 } }}
            />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={800}
              sx={{
                fontSize: {
                  xs: "1.25rem",
                  sm: "1.65rem",
                  md: "2rem",
                },
                lineHeight: 1.1,
                letterSpacing: "-0.025em",
              }}
            >
              Workshop Dashboard
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.3,
                fontSize: { xs: "0.72rem", sm: "0.82rem" },
              }}
            >
              Railway coach repair & POH operations
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={0.8}
          flexWrap="wrap"
          useFlexGap
          sx={{
            width: { xs: "100%", md: "auto" },
          }}
        >
          <Chip
            icon={<EventAvailableIcon />}
            label="27 Sep 2026"
            variant="outlined"
            sx={{
              bgcolor: "background.paper",
              fontWeight: 600,
              height: 30,
            }}
          />

          <Chip
            label="WORKSHOP ACTIVE"
            color="success"
            variant="outlined"
            sx={{
              fontWeight: 700,
              height: 30,
            }}
          />
        </Stack>
      </Box>

      {/* ================================================= */}
      {/* KPI AREA */}
      {/* ================================================= */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(3, minmax(0, 1fr))",
            lg: "repeat(6, minmax(0, 1fr))",
          },
          gap: {
            xs: 1.1,
            sm: 1.5,
            lg: 1.75,
          },
          width: "100%",
        }}
      >
        {stats.map((item) => (
          <Box key={item.title} sx={{ minWidth: 0, display: "flex" }}>
            <KPI item={item} />
          </Box>
        ))}
      </Box>

      {/* ================================================= */}
      {/* CURRENT COACHES + SHOP WORKLOAD */}
      {/* ================================================= */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 2fr) minmax(320px, 1fr)",
          },
          gap: { xs: 1.5, sm: 2 },
          mt: { xs: 1.5, sm: 2 },
          alignItems: "stretch",
        }}
      >
        <CurrentCoaches isMobile={isMobile} />
        <ShopWorkload />
      </Box>

      {/* ================================================= */}
      {/* MOVEMENTS + ACTIVITY */}
      {/* ================================================= */}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 2fr) minmax(320px, 1fr)",
          },
          gap: { xs: 1.5, sm: 2 },
          mt: { xs: 1.5, sm: 2 },
          alignItems: "stretch",
        }}
      >
        <RecentMovements isMobile={isMobile} />
        <RecentActivity />
      </Box>

      {/* ================================================= */}
      {/* DAILY SUMMARY */}
      {/* ================================================= */}

      <DailySummary />

      {/* ================================================= */}
      {/* FOOTER */}
      {/* ================================================= */}

      <Box
        sx={{
          mt: { xs: 1.5, sm: 2 },
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          textAlign: { xs: "center", sm: "right" },
          px: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Workshop Management System • Operational Dashboard
        </Typography>
      </Box>
    </Box>
  );
}
